<template>
  <div class="lti-root">
    <div class="live-time-indicator">
      <button
        class="lti-open-btn"
        type="button"
        :aria-label="t('liveMode.ariaLabel', { label, countdown })"
        @click="emit('open')"
      >
        <div class="lti-main">
          <div class="lti-dot-wrapper" aria-hidden="true">
            <span class="lti-dot-pulse" />
            <span class="lti-dot" />
          </div>
          <span class="lti-label">{{ label }}</span>
          <span class="lti-timezone">({{ timezone }})</span>
          <icon-calendar class="lti-calendar-icon" color="shamrock" />
        </div>
      </button>

      <div class="lti-divider" aria-hidden="true" />

      <div class="lti-controls">
        <span class="lti-countdown" aria-hidden="true">{{ countdown }}s</span>
        <button
          class="lti-close-btn"
          type="button"
          :title="t('liveMode.stopLabel')"
          :aria-label="t('liveMode.stopLabel')"
          @click.stop="emit('close')"
        >
          <icon-close color="jewel" />
        </button>
      </div>
    </div>

    <div class="lti-progress-track" aria-hidden="true">
      <div class="lti-progress-fill" :style="{ width: progressPercent + '%' }" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import IconCalendar from 'ui-component-library/assets/icons/IconCalendar.vue';
import IconClose from 'ui-component-library/assets/icons/IconClose.vue';

interface Props {
  label: string;
  timezone: string;
  countdown: number;
  totalInterval: number;
}

const props = defineProps<Props>();

const emit = defineEmits<{ (e: 'close'): void; (e: 'open'): void }>();

const { t } = useI18n();

const progressPercent = computed(() => {
  if (!props.totalInterval) return 100;
  return Math.max(0, Math.min(100, (props.countdown / props.totalInterval) * 100));
});
</script>

<style lang="scss" scoped>
@use 'ui-component-library/assets/styles/colors' as colors;
@use 'sass:map';

$shamrock: map.get(colors.$colors, 'shamrock');
$shamrock-0: map.get(colors.$colors, 'shamrock-0');
$shamrock-15: map.get(colors.$colors, 'shamrock-15');
$shamrock-70: map.get(colors.$colors, 'shamrock-70');
$white: map.get(colors.$colors, 'white');
$hint-of-green: map.get(colors.$colors, 'hint-of-green');
$tara: map.get(colors.$colors, 'tara');
$feta: map.get(colors.$colors, 'feta');
$zanah: map.get(colors.$colors, 'zanah');

.lti-root {
  display: inline-flex;
  flex-direction: column;
}

.live-time-indicator {
  align-items: center;
  background: linear-gradient(180deg, $hint-of-green 0%, $white 100%);
  border: 1px solid $shamrock;
  border-radius: 4px;
  box-shadow: 0 0 0 1px $shamrock-15;
  display: inline-flex;
  gap: 2px;
  height: 38px;
  padding: 1px;

  &:hover {
    background: linear-gradient(180deg, $tara 0%, $feta 100%);
  }
}

.lti-open-btn {
  background: transparent;
  border: 0;
  cursor: pointer;
  display: flex;
  flex: 1 0 0;
  padding: 0;
  user-select: none;
}

.lti-progress-track {
  background: map.get(colors.$colors, 'zanah');
  border-radius: 0 0 4px 4px;
  height: 2px;
  overflow: hidden;
  width: 100%;
}

.lti-progress-fill {
  background: map.get(colors.$colors, 'shamrock');
  height: 100%;
  transition: width 1s linear;
}

.lti-main {
  align-items: center;
  display: flex;
  flex: 1 0 0;
  gap: 6px;
  padding: 0 8px 0 12px;
}

.lti-dot-wrapper {
  flex-shrink: 0;
  height: 8px;
  position: relative;
  width: 8px;
}

/* box-shadow animation avoids clipping by overflow:hidden on ancestors */
.lti-dot-pulse {
  border-radius: 50%;
  display: block;
  height: 8px;
  left: 0;
  position: absolute;
  top: 0;
  width: 8px;
  animation: lti-ping 1.5s ease-out infinite;
}

.lti-dot {
  animation: lti-glow 1.5s ease-in-out infinite;
  background: map.get(colors.$colors, 'shamrock');
  border-radius: 50%;
  display: block;
  height: 8px;
  left: 0;
  position: absolute;
  top: 0;
  width: 8px;
}

@keyframes lti-ping {
  0% {
    box-shadow: 0 0 0 0 $shamrock-70;
  }

  70% {
    box-shadow: 0 0 0 6px $shamrock-0;
  }

  100% {
    box-shadow: 0 0 0 0 $shamrock-0;
  }
}

@keyframes lti-glow {
  0%,
  100% {
    opacity: 1;
  }

  50% {
    opacity: 0.65;
  }
}

.lti-label {
  color: map.get(colors.$colors, 'san-felix');
  font-size: 14px;
  font-weight: 500;
  line-height: 21px;
  white-space: nowrap;
}

.lti-timezone {
  color: map.get(colors.$colors, 'dusty-gray');
  font-size: 11px;
  font-weight: 500;
  line-height: 16.5px;
  white-space: nowrap;
}

.lti-calendar-icon {
  display: block;
  flex-shrink: 0;
  height: 20px;
  width: 20px;
}

.lti-divider {
  align-self: center;
  border-left: 1px solid $zanah;
  flex-shrink: 0;
  height: 28px;
}

.lti-controls {
  align-items: center;
  display: flex;
  flex-shrink: 0;
  gap: 2px;
  padding: 0 4px 0 9px;
}

.lti-countdown {
  color: map.get(colors.$colors, 'san-felix');
  font-size: 12px;
  font-weight: 500;
  line-height: 18px;
  min-width: 28px;
  text-align: center;
}

.lti-close-btn {
  align-items: center;
  background: map.get(colors.$colors, 'zanah');
  border: 0;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  flex-shrink: 0;
  height: 20px;
  justify-content: center;
  padding: 0;
  transition: background-color 0.15s ease;
  width: 20px;

  &:hover {
    background: map.get(colors.$colors, 'shamrock');

    :deep(svg) {
      fill: map.get(colors.$colors, 'white');
    }
  }

  :deep(svg) {
    display: block;
    height: 12px;
    width: 12px;
  }
}
</style>
